import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as CarActions from './car.action';
import { CarService } from './car.service';
import { BannerService } from '../../shared/services/banner.service';
import * as CarTypeModels from '../../models/car/carType';
import * as CarModels from '../../models/car/car';
import { CarSearchModel } from '../../models/car/carSearch';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import { empty } from 'rxjs';
import { SaveState } from '../../models/commonModels';
import { saveAs } from 'file-saver';
import * as Models from '../../models/commonModels'
import * as BaseStateModels from '../BaseState';
import { StorageService } from '../../shared/services/storage.service';

export interface CarStateModel extends BaseStateModels.BaseStateInterface {
  CarTypes: CarTypeModels.CarTypeListModel;
  Car: CarModels.CarModel;
  Save: SaveState;
  Search: CarSearchModel;
  List: CarModels.CarListModel;
  SearchBarList: CarModels.CarListModel;
  MarkedRegistrationNumbers: string[];
  ImportLoading: boolean;
  ShowImportPanel: boolean;
  ImportResponse: CarModels.CarUpdateResponse;
  CarTyreDetail: Models.Response<CarModels.CarTyreDetailModel>;
  Dictionary: Models.Response<CarModels.CarModel[]>;
  CarResponse: Models.Response<CarModels.CarModel>;
  CarNotes: Models.ResponseList<CarModels.CarNoteModel>;
  ImportReRegistration: number;
}

class DictionaryKeys {
  public static readonly CarTypes = "CarTypes";
}

@State<CarStateModel>({
  name: 'carState',
  defaults: {
    CarTypes: null,
    Car: null,
    Save: null,
    Search: null,
    List: null,
    SearchBarList: null,
    MarkedRegistrationNumbers: null,
    ImportLoading: null,
    ShowImportPanel: null,
    ImportResponse: null,
    CarTyreDetail: null,
    Report: null,
    Dictionary: null,
    CarResponse: null,
    CarNotes: null,
    ImportReRegistration: null
  }
})

@Injectable()

export class CarState extends BaseStateModels.BaseState<CarStateModel>  {

  constructor(
    private helperService: HelperService,
    private carService: CarService,
    private bannerService: BannerService,
    private storageService: StorageService
  ) {
    super();
  }


  @Selector()
  static ShowImportPanel(state: CarStateModel) {
    return state.ShowImportPanel;
  }

  @Selector()
  static ImportResponse(state: CarStateModel) {
    return state.ImportResponse;
  }

  @Selector()
  static MarkedRegistrationNumbers(state: CarStateModel) {
    return state.MarkedRegistrationNumbers;
  }

  @Selector()
  static CarTypes(state: CarStateModel) {
    return state.CarTypes;
  }

  @Selector()
  static List(state: CarStateModel) {
    return state.List;
  }

  @Selector()
  static Car(state: CarStateModel) {
    return state.Car;
  }

  @Selector()
  static Save(state: CarStateModel) {
    return state.Save;
  }

  @Selector()
  static Search(state: CarStateModel) {
    return state.Search;
  }

  @Selector()
  static CarTyreDetail(state: CarStateModel) {
    return state.CarTyreDetail;
  }

  @Selector()
  static SearchBarList(state: CarStateModel) {
    return state.SearchBarList;
  }

  @Selector()
  static Report(state: CarStateModel) {
    return state.Report;
  }

  @Selector()
  static Dictionary(state: CarStateModel) {
    return state.Dictionary;
  }

  @Selector()
  static CarResponse(state: CarStateModel) {
    return state.CarResponse;
  }

  @Selector()
  static CarNotes(state: CarStateModel) {
    return state.CarNotes;
  }

  @Selector()
  static ImportReRegistration(state: CarStateModel) {
    return state.ImportReRegistration;
  }


  @Action(CarActions.GetCarTypeDictionary)
  GetCarTypeDictionary(
    context: StateContext<CarStateModel>,
    action: CarActions.GetCarTypeDictionary
  ) {

    const errorMessage = 'Błąd pobierania typów samochodów.';

    let storageKey = DictionaryKeys.CarTypes;

    context.setState(
      patch({
        CarTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CarTypes) {

      this.carService.getCarTypeDictionary().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CarTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(errorMessage);
        }
      },
        (error) => {

          this.helperService.SaveException('CarActions.GetCarTypeDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(CarActions.Get)
  Get(
    context: StateContext<CarStateModel>,
    action: CarActions.Get
  ) {
    context.setState(
      patch({
        Car: null
      })
    );

    const errorMessage = 'Błąd pobierania szczegółów samochodu.';

    this.carService.getCar(action.CarId).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Car: ret.Item
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.GetCar', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.Set)
  Set(
    context: StateContext<CarStateModel>,
    action: CarActions.Set
  ) {
    const errorMessage = 'Błąd zapisu szczegółów samochodu.';

    this.carService.setCar(action.Car).subscribe(
      ret => {
        this.bannerService.success('Zmiany szczegółów samochodu zapisane.');

        context.setState(
          patch({
            Save: new SaveState(action.Car.CarId, true)
          })
        );
      },

      (error) => {

        context.setState(
          patch({
            Save: new SaveState(action.Car.CarId, false)
          })
        );

        this.helperService.SaveException('CarActions.SetCar', error);

        if (error) {
          this.bannerService.error(error);
        }
        else {
          this.bannerService.error(errorMessage);
        }
      },
      () => { });
  }

  @Action(CarActions.SetSearch)
  SetSearch(
    context: StateContext<CarStateModel>,
    action: CarActions.SetSearch
  ) {

    action.Search.CurrentPage = 1;
    action.Search.MarkAll = false;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new CarActions.GetList(action.Search));
  }

  @Action(CarActions.SetListPage)
  SetListPage(
    context: StateContext<CarStateModel>,
    action: CarActions.SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new CarActions.GetList(action.Search));
  }

  @Action(CarActions.ResetSearch)
  ResetSearch(
    context: StateContext<CarStateModel>
  ) {

    const state = context.getState();

    const search = new CarSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new CarActions.GetList(search));
  }

  @Action(CarActions.DisposeSearch)
  DisposeSearch(
    context: StateContext<CarStateModel>
  ) {

    context.setState(
      patch({
        Search: null
      }));
  }

  @Action(CarActions.GetList)
  GetList(
    context: StateContext<CarStateModel>,
    action: CarActions.GetList
  ) {

    const errorMessage = 'Błąd pobierania listy samochodów';

    context.setState(
      patch({
        List: null
      })
    );

    this.carService.get(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            List: ret
          })
        );
        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.Insert)
  Insert(
    context: StateContext<CarStateModel>,
    action: CarActions.Insert
  ) {
    const errorMessage = 'Błąd zapisu nowego samochodu.';

    this.carService.insert(action.Car).subscribe(
      ret => {
        this.bannerService.success('Zapisano nowy samochód.');

        context.setState(
          patch({
            Save: new SaveState(action.Car.Item.CarId, true)
          })
        );
      },

      (error) => {
        context.setState(
          patch({
            Save: new SaveState(action.Car.Item.CarId, false)
          })
        );


        this.helperService.SaveException('CarActions.Insert', error);

        this.bannerService.error(`${errorMessage} ${error}`);
      },
      () => { });
  }

  @Action(CarActions.ResetSave)
  ResetSave(
    context: StateContext<CarStateModel>,
  ) {
    context.setState(
      patch({
        Save: null
      })
    );
  }

  @Action(CarActions.SetMarkList)
  SetMarkList(
    context: StateContext<CarStateModel>,
    action: CarActions.SetMarkList
  ) {
    context.getState();

    const errorMessage = 'Zaimportowany plik nie zawierał poprawnych danych.';

    context.setState(
      patch({
        MarkedRegistrationNumbers: null
      })
    );

    if (action.RegistrationNumbers) {
      if (action.RegistrationNumbers.length > 0) {
        context.setState(
          patch({
            MarkedRegistrationNumbers: action.RegistrationNumbers
          })
        );

      } else {
        this.bannerService.error(errorMessage);
      }
    }
  }

  @Action(CarActions.BlockMarked)
  BlockMarked(
    context: StateContext<CarStateModel>,
    action: CarActions.BlockMarked
  ) {
    const errorMessage = 'Błąd blokowania listy samochodów.';
    context.getState();
    let reload = true;
    if (action.ReloadAfterSuccessUpdate === false) {
      reload = action.ReloadAfterSuccessUpdate;
    }

    this.carService.blockCars(action.CarsUpdate).subscribe(
      ret => {
        this.bannerService.success('Zmiany szczegółów samochodu zapisane.');
        if (reload === true) {
          context.setState(
            patch({
              Save: new SaveState(null, true, ret.RequestSignature)
            })
          );
        }
      },

      (error) => {

        context.setState(
          patch({
            Save: new SaveState(null, false, error.RequestSignature)
          })
        );


        this.helperService.SaveException('CarActions.BlockMarked', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.UnblockMarked)
  UnblockMarked(
    context: StateContext<CarStateModel>,
    action: CarActions.BlockMarked
  ) {
    const errorMessage = 'Błąd odblokowywania listy samochodów.';
    context.getState();
    let reload = true;
    if (action.ReloadAfterSuccessUpdate === false) {
      reload = action.ReloadAfterSuccessUpdate;
    }

    this.carService.unblockCars(action.CarsUpdate).subscribe(
      ret => {
        this.bannerService.success('Zmiany szczegółów samochodu zapisane.');
        if (reload === true) {
          context.setState(
            patch({
              Save: new SaveState(null, true, ret.RequestSignature)
            })
          );
        }

      },

      (error) => {
        context.setState(
          patch({
            Save: new SaveState(null, false, error.RequestSignature)
          })
        );


        this.helperService.SaveException('CarActions.UnblockMarked', error);
        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.ChangeRegistrations)
  ChangeRegistrations(
    context: StateContext<CarStateModel>,
    action: CarActions.ChangeRegistrations
  ) {
    let errorMessage = 'Błąd zmiany numerów rejestracyjnych. ';
    this.carService.changeRegistrations(action.formData).subscribe(ret => {
      if (ret.Success) {
        this.bannerService.success('Zmiany szczegółów samochodów zostały zapisane.');
        context.setState(
          patch({
            Save: new SaveState(null, true, ret.RequestSignature)
          })
        );
      } else {
        context.setState(
          patch({
            Save: new SaveState(null, false, ret.RequestSignature)
          })
        );
        if (ret.ImproperRegistrations && ret.ImproperRegistrations.length > 0) {
          errorMessage += ret.Message;
        }
        this.helperService.SaveException('CarActions.ChangeRegistrations', errorMessage);
        this.bannerService.error(errorMessage);
      }
    }
    );
  }

  @Action(CarActions.ChangeCustomer)
  ChangeCustomer(
    context: StateContext<CarStateModel>,
    action: CarActions.BlockMarked
  ) {
    const errorMessage = 'Błąd zmiany klienta.';

    this.carService.changeCustomer(action.CarsUpdate).subscribe(
      ret => {
        this.bannerService.success('Zmiany szczegółów samochodu zapisane.');

        context.setState(
          patch({
            Save: new SaveState(null, true, ret.RequestSignature)
          })
        );
      },

      (error) => {
        context.setState(
          patch({
            Save: new SaveState(null, false, error.RequestSignature)
          })
        );

        this.helperService.SaveException('CarActions.ChangeCustomer', error);
        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.GetFilteredReport)
  GetFilteredReport(
    context: StateContext<CarStateModel>,
    action: CarActions.GetFilteredReport
  ) {

    this.PatchReportContext(action.ReportName, true, context);

    const fileName = 'Raport-samochody';
    this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

    this.carService.getFilteredReport(action.Search).subscribe(
      res => {
        const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(file);

        this.PatchReportContext(action.ReportName, false, context);
      },
      res => {
        this.PatchReportContext(action.ReportName, false, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        this.PatchReportContext(action.ReportName, false, context);

        this.helperService.consoleLog(' @Action(SpecificationActions.GetSpecificationReportReplacementAndSeasoning)');
      }
    );
  }

  @Action(CarActions.GetMarkedReport)
  GetMarkedReport(
    context: StateContext<CarStateModel>,
    action: CarActions.GetMarkedReport
  ) {


    this.PatchReportContext(action.ReportName, true, context);


    const fileName = 'Raport-samochody';
    if (action.Marked.CarRegistrationNumbers.length === 0 && action.Marked.CarIds.length === 0) {
      this.bannerService.error('Aby rozpocząć generowanie raportu, należy zaznaczyć co najmniej jeden samochód.');

      this.PatchReportContext(action.ReportName, false, context);

    } else {
      this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

      this.carService.getMarkedReport(action.Marked).subscribe(
        res => {
          const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

          saveAs(file);

          this.PatchReportContext(action.ReportName, false, context);
        },
        res => {
          this.PatchReportContext(action.ReportName, false, context);

          this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
        },
        () => {
          this.PatchReportContext(action.ReportName, false, context);

          this.helperService.consoleLog(' @Action(SpecificationActions.GetSpecificationReportReplacementAndSeasoning)');
        }
      );
    }
  }

  @Action(CarActions.ShowImportPanel)
  ShowImportPanel(
    context: StateContext<CarStateModel>,
    action: CarActions.ShowImportPanel
  ) {
    context.setState(
      patch({
        ShowImportPanel: action.show
      })
    );
  }

  @Action(CarActions.ImportData)
  ImportData(
    context: StateContext<CarStateModel>,
    action: CarActions.ImportData
  ) {
    context.getState();

    function patchLoading(b: boolean) {
      context.setState(
        patch({
          ImportLoading: b,
        })
      );
    }
    function patchShow(b: boolean) {
      context.setState(
        patch({
          ShowImportPanel: b,
        })
      );
    }
    function patchSave(s: SaveState) {
      context.setState(
        patch({
          Save: s,
        })
      );
    }

    function patchImportResponse(r: CarModels.CarUpdateResponse) {
      context.setState(
        patch({
          ImportResponse: r,
        })
      );
    }

    patchLoading(true);
    let errorMessage = 'Błąd importu danych samochodów.';
    this.carService.importData(action.formData).subscribe(ret => {
      patchImportResponse(ret);
      if (ret.Success) {
        this.bannerService.success('Zaimportowano dane samochodów.');
        patchLoading(false);
        if (ret.ImproperData?.length > 0) {
          patchShow(true);
        } else {
          patchShow(false);
        }
        patchSave(new SaveState(null, true, ret.RequestSignature));
      } else {
        patchLoading(false);
        patchSave(new SaveState(null, false, ret.RequestSignature));
        if (ret.ImproperRegistrations && ret.ImproperRegistrations.length > 0) {
          errorMessage += ret.Message;
          this.bannerService.error(errorMessage);
        }
        if (ret.ImproperData && ret.ImproperData.length > 0) {
          /*ret.ImproperData.forEach((d) => {
            this.bannerService.error(`${d.Name} - ${d.Description}; `);
          });*/
          this.bannerService.error(errorMessage);
        }
        this.helperService.SaveException('CarActions.ImportData', errorMessage);
      }
    }
    );
  }

  @Action(CarActions.CheckNumbers)
  CheckNumbers(
    context: StateContext<CarStateModel>,
    action: CarActions.CheckNumbers
  ) {
    const errorMessage = 'Błąd odczytu tablicy Cars.';

    this.carService.checkNumbers(action.Numbers).subscribe(
      ret => {
        if (ret.Numbers.length === 0) {
          this.bannerService.error('Podane numery nie istnieją w bazie danych.');
        } else if (ret.Numbers.length === action.Numbers.length) {
          this.bannerService.success(`Zaznaczono ${ret.Numbers.length} numerów.`);
        } else {
          this.bannerService.warning(`Zaznaczono ${ret.Numbers.length} numerów z ${action.Numbers.length} odczytanych z pliku!.`);
        }

      },

      (error) => {

        this.helperService.SaveException('CarActions.CheckNumbers', error);
        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.ClearErrorList)
  ClearErrorList(
    context: StateContext<CarStateModel>
  ) {

    const s = context.getState();
    context.setState(
      patch({
        ImportResponse: null
      }));
  }

  @Action(CarActions.GetCarTyreDetail)
  GetCarTyreDetail(
    context: StateContext<CarStateModel>,
    action: CarActions.GetCarTyreDetail
  ) {
    context.setState(
      patch({
        Car: null
      })
    );

    const errorMessage = 'Błąd pobierania szczegółów zamontowanych opon dla samochodu.';

    this.carService.getCarTyreDetail(action.CarId).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            CarTyreDetail: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.GetCarTyreDetail', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.SendEmailMessage)
  SendEmailMessage(
    context: StateContext<CarStateModel>,
    action: CarActions.SendEmailMessage
  ) {
    const errorMessage = "Błąd wysyłania wiadomości e-mail.";

    this.carService.sendEmail(action.Message).subscribe(
      (ret) => {
        if (ret.Success == true)
          this.bannerService.success("Wiadmość email została wysłana.");

        if (ret.Success == false) {
          this.bannerService.error(ret.Message);
        }
      },
      (error) => {
        this.helperService.SaveException("CarActions.SendEmailMessage", error);

        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(CarActions.SendSMSMessage)
  SendSMSMessage(
    context: StateContext<CarStateModel>,
    action: CarActions.SendSMSMessage
  ) {
    const errorMessage = "Błąd wysyłania wiadomości sms.";

    this.carService.sendSMS(action.Message).subscribe(
      (ret) => {
        if (ret.Success == true)
          this.bannerService.success("Wiadmość SMS została wysłana.");

        if (ret.Success == false) {
          this.bannerService.error(ret.Message);
        }
      },
      (error) => {
        this.helperService.SaveException("CarActions.SendSMSMessage", error);

        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(CarActions.SendSMSMessageFiltered)
  SendSMSMessageFiltered(
    context: StateContext<CarStateModel>,
    action: CarActions.SendSMSMessageFiltered
  ) {
    const errorMessage = "Błąd wysyłania wiadomości sms.";

    this.carService.sendSMSFiltered(action.SMS).subscribe(
      (ret) => {
        if (ret.Success == true)
          this.bannerService.success("Wiadmość SMS została wysłana.");

        if (ret.Success == false) {
          this.bannerService.error(ret.Message);
        }
      },
      (error) => {
        this.helperService.SaveException("CarActions.SendSMSMessageFiltered", error);

        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(CarActions.SendSMSMessageMarked)
  SendSMSMessageMarked(
    context: StateContext<CarStateModel>,
    action: CarActions.SendSMSMessageMarked
  ) {
    const errorMessage = "Błąd wysyłania wiadomości sms.";

    this.carService.sendSMSMarked(action.SMS).subscribe(
      (ret) => {
        if (ret.Success == true)
          this.bannerService.success("Wiadmość SMS została wysłana.");

        if (ret.Success == false) {
          this.bannerService.error(ret.Message);
        }
      },
      (error) => {
        this.helperService.SaveException("CarActions.SendSMSMessageFiltered", error);

        this.bannerService.error(errorMessage);
      }
    );
  }

  @Action(CarActions.GeSearchBarList)
  GeSearchBarList(
    context: StateContext<CarStateModel>,
    action: CarActions.GeSearchBarList
  ) {

    const errorMessage = 'Błąd pobierania listy samochodów';

    context.setState(
      patch({
        SearchBarList: null
      })
    );

    this.carService.getSearchBar(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            SearchBarList: ret
          })
        );
        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.GeSearchBartList', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.ImportCars)
  Import(
    context: StateContext<CarStateModel>,
    action: CarActions.ImportCars
  ) {

    this.bannerService.quickInfo('Rozpoczęto import samochodów, proszę czekać.');

    this.PatchReportContext(action.ReportName, true, context);

    this.carService.importCars(action.Data).subscribe(ret => {

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      this.PatchReportContext(action.ReportName, false, context);

      if (result === 'OK') {

        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.FileName}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);

      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('CarActions.ImportCars', error);

        this.bannerService.error('Błąd importu zleceń zamówień opon.');

        this.PatchReportContext(action.ReportName, false, context);
      },
      () => {
        this.PatchReportContext(action.ReportName, false, context);

        this.helperService.consoleLog(
          "@Action(CarActions.ImportCars)"
        );
      }
    );
  }

  @Action(CarActions.GetDictionary)
  GetDictionary(
    context: StateContext<CarStateModel>,
    action: CarActions.GetDictionary
  ) {
    context.setState(
      patch({
        Dictionary: null
      })
    );

    const errorMessage = 'Błąd pobierania słownika samochodów.';

    this.carService.getDictionary(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Dictionary: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.GetDictionary', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.Delete)
  Delete(
    context: StateContext<CarStateModel>,
    action: CarActions.Delete
  ) {
    context.setState(
      patch({
        CarResponse: null
      })
    );

    const errorMessage = 'Błąd usuwania wybranego samochodu.';

    this.carService.delete(action.Car).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            CarResponse: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.Delete', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.Create)
  Create(
    context: StateContext<CarStateModel>,
    action: CarActions.Delete
  ) {
    context.setState(
      patch({
        CarResponse: null
      })
    );

    const errorMessage = 'Błąd dodawania nowego samochodu.';

    this.carService.Create(action.Car).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            CarResponse: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.Create', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.Merge)
  Merge(
    context: StateContext<CarStateModel>,
    action: CarActions.Merge
  ) {
    context.setState(
      patch({
        CarResponse: null
      })
    );

    const errorMessage = 'Błąd łaczenia samochodów.';

    this.carService.Merge(action.Cars).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            CarResponse: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('CarActions.Merge', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(CarActions.GetCarNotes)
  GetCarNotes(
    context: StateContext<CarStateModel>,
    action: CarActions.GetCarNotes
  ) {
    const errorMessage = 'Błąd odczytu wiadomości camochodu.';

    this.carService.getCarNotes(action.CarId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              CarNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('CarActions.GetCarNotes', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.SetCarNote)
  SetCarNote(
    context: StateContext<CarStateModel>,
    action: CarActions.SetCarNote
  ) {
    const errorMessage = 'Błąd zapisu wiadomości dla camochodu.';

    this.carService.setCarNote(action.Note).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              CarNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('CarActions.SetCarNote', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(CarActions.ImportReRegistration)
  ImportReRegistration(
    context: StateContext<CarStateModel>,
    action: CarActions.ImportReRegistration
  ) {

    function patchContext() {
      context.setState(
        patch({
          ImportReRegistration: Math.random()
        })
      );
    }


    this.bannerService.quickInfo('Rozpoczęto import listy samochodów do przerejestrowania.');

    this.carService.ImportReRegistration(action.ImportFile).subscribe(ret => {


      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {
        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.ImportFile.name}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        saveAs(file);

      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.ImportFile.name}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('CarActions.ImportReRegistration', error);

        this.bannerService.error('Błąd importu listy samochodów do przerejestrowania.');
      },
      () => {
        patchContext();
      }
    );
  }
}

